import { GIButton } from 'app-components';
import React, { FC, useContext } from 'react';
import { FormattedMessage } from 'react-intl/dist';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
    fetchEnrollmentAndEnrolleeDetails,
    updateEnrollmentDetails,
} from '../../../../actions/memberPortal/enrollmentsActionCreators';
import { GICheckbox, GIFormControl, GIFormLabel } from '../../../../components/common/app-components/GIInput';
import { ConditionalRendering } from '../../../../components/common/conditional-rendering';
import { TXN_REASON, TXN_TYPES } from '../../../../constants/enrollmentList/enrollmentList.constant';
import { GENDER_TYPES } from '../../../../constants/ssap/miscellaneous/miscellaneous';
import { useAppDispatch } from '../../../../hooks';
import useSetSelectedEnrollment from '../../../../hooks/useSetSelectedEnrollment';
import { formatDateToMMDDYYYY } from '../../../../utils/dates/dateMethods';
import useGetSelectedEnrollmentDetails from '../../hooks/useGetSelectedEnrollmentDetails';
import { changeFieldValue } from '../common/enrollmentUtils';
import { getEnrollmentEditToolErrorMessage } from '../common/validation';
import { AddOrChangeTxnFormContext } from '../context/AddOrChangeTxnProvider';
import { EnrollmentContext } from '../context/MemberMyEnrollmentProvider';
import useEnrollmentModal from '../hooks/useEnrollmentModal';
import { StyledCheckboxField, StyledFooterContainer } from '../styles/EnrollmentSharedStyled';

const AddOeChangeTxnFooter: FC = () => {
    const dispatch = useAppDispatch();
    const { id } = useParams();
    const getSelectedEnrollmentDetails = useGetSelectedEnrollmentDetails();
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { userPermissions } = permissions || {};
    const {
        watch,
        setValue,
        formState: { isValid, isDirty },
    } = useContext(AddOrChangeTxnFormContext);
    const setSelectedEnrollment = useSetSelectedEnrollment();

    const { selectedEnrollment, isEdit, setIsEdit, setViewAddOrChangeTxn } = useContext(EnrollmentContext);
    const values = watch();
    const { handleShowResult } = useEnrollmentModal();

    const handleClose = () => {
        setIsEdit(false);
        setViewAddOrChangeTxn(false);
    };

    const handleCheckBoxClick = (field, value) => {
        changeFieldValue(setValue, field, value);
    };

    const validateAndSaveAddOrChangeTxn = () => {
        if (isEdit == false) {
            sendAsAddTransaction();
        } else if (isEdit == true) {
            sendAsChangeTransaction();
        }
    };

    const sendAsAddTransaction = async () => {
        let data: any = {
            id: values.enrollmentId,
            householdId: selectedEnrollment.houseHoldCaseId,
            comment: values.addOrChangeComment,
            send834: values.addOrChangeSendIn834,
            sendTermNotice: values.sendTermNoticeVM,
            benefitEffectiveDateStr: values.enrollmentBenefitEffectiveStartDateUI,
            aptcAmount: values.aptcAmount != '' ? values.aptcAmount : null,
            action: TXN_TYPES.Add,
            members: [],
        };

        values.enrolleeDataDtoList.forEach(function (value) {
            if (value.txnType === true) {
                data.members.push({
                    exchgIndivIdentifier: value.memberId,
                    enrolleeId: value.enrolleeId,
                });
            }
        });
        const response = await updateEnrollmentDetails(dispatch, data);
        onResponse(response);
    };

    const sendAsChangeTransaction = async () => {
        let data: any = {
            id: values.enrollmentId,
            householdId: selectedEnrollment.houseHoldCaseId,
            comment: values.addOrChangeComment,
            send834: values.addOrChangeSendIn834,
            sendTermNotice: values.sendTermNoticeVM,
            benefitEffectiveDate: null,
            benefitEndDate: null,
            benefitEffectiveDateStr: values.enrollmentBenefitEffectiveStartDateUI,
            benefitEndDateStr: values.enrollmentBenefitEffectiveEndDateUI,
            confirmIfNotConfirmed: false,
            premiumPaidToDateEndStr: formatDateToMMDDYYYY(values.enrollmentLastPremPaidThroughDateUI),
            premiumPaidToDateEnd: null,
            members: [],
            updateSLCP: false,
            enrollmentPremiumDtoList: values.enrollmentPremiumDtoList,
        };

        if (values.enrollmentLevelTxnType == TXN_TYPES.Term) {
        let txnReason = TXN_REASON.AI;
        values.enrolleeDataDtoList.forEach(function (enrollee) {
            enrollee.txnReason = txnReason;
        });
        }

        values.enrolleeDataDtoList.forEach(function (value) {
            data.members.push({
                exchgIndivIdentifier: value.memberId,
                enrolleeId: value.enrolleeId,
                txnType: value.txnType,
                txnReason: value.txnReason,
                effectiveStartDateStr: value.enrolleeEffectiveStartDateUI,
                effectiveEndDateStr: value.enrolleeEffectiveEndDateUI,
                firstName: value.firstName,
                lastName: value.lastName,
                middleName: value.middleName,
                gender: genderLookupCode(value),
                birthDate: new Date(value.birthDate).toLocaleDateString(),
                homeZip: value.enrolleeZipcode,
            });
        });

        var newPremium = values.enrollmentPremiumDtoList;
        var oldPremium = selectedEnrollment.enrollmentPremiumDtoList;
        data.updateSLCP = newPremium.some(
            (newPrem, i) =>
                newPrem.month === oldPremium[i].month &&
                newPrem.slcspPremiumAmount !== oldPremium[i].slcspPremiumAmount,
        );

        const response = await updateEnrollmentDetails(dispatch, data);
        onResponse(response);
    };

    const onResponse = async response => {
        if (response.errCode === 200) {
            const message = getEnrollmentEditToolErrorMessage(response.errCode, '');
            handleShowResult({
                isSuccess: true,
                isError: false,
                message: message,
                handleSubmit: onModalClose,
            });

            const formData = new FormData();
            formData.append('encHouseholdId', id);
            formData.append('csrftoken', window.csrf_token);
            const enrollmentDetails = await dispatch(fetchEnrollmentAndEnrolleeDetails(formData));
            if (enrollmentDetails) {
                const enrollment = await getSelectedEnrollmentDetails(
                    selectedEnrollment.enrollmentId,
                    enrollmentDetails,
                );
                setSelectedEnrollment(enrollment);
            }
        } else {
            const message = getEnrollmentEditToolErrorMessage(response.errCode, '');
            handleShowResult({
                isSuccess: false,
                isError: true,
                message: message,
            });
        }
    };

    const onModalClose = () => {
        setIsEdit(false);
        setViewAddOrChangeTxn(false);
    };

    const genderLookupCode = (value: any) => {
        switch (value.genderLookupLabel.toLowerCase()) {
            case GENDER_TYPES.MALE:
                return 'M';
            case GENDER_TYPES.FEMALE:
                return 'F';
            default:
                return 'U';
        }
    };

    return (
        <>
            <StyledFooterContainer data-testid="footer-container">
                <StyledCheckboxField>
                    <GIFormControl
                        id="sendInTxnTypeForm"
                        name={`member.enrollment_list.premiumhistory.sendIn`}
                        type="checkbox"
                    >
                        <GICheckbox
                            id={`member.enrollment_list.premiumhistory.sendIn`}
                            checked={values.addOrChangeSendIn834}
                            onChange={e => handleCheckBoxClick('addOrChangeSendIn834', e.target.checked)}
                            name={`member.enrollment_list.premiumhistory.sendIn`}
                            value={values.addOrChangeSendIn834}
                            disabled={values.isTermReasonNonPayment}
                        />
                        <GIFormLabel
                            id={`member.enrollment_list.premiumhistory.sendIn`}
                            name={`member.enrollment_list.premiumhistory.sendIn`}
                        >
                            <FormattedMessage id="member.enrollment_list.premiumhistory.sendIn" />
                        </GIFormLabel>
                    </GIFormControl>
                </StyledCheckboxField>

                <ConditionalRendering isTrue={userPermissions.sendTermFlag}>
                    <StyledCheckboxField>
                        <GIFormControl id="sendInTxnTypeForm" name={`sendTermNoticeVM`} type="checkbox">
                            <GICheckbox
                                id={`sendTermNoticeVM`}
                                checked={values.sendTermNoticeVM}
                                onChange={e => handleCheckBoxClick('sendTermNoticeVM', e.target.checked)}
                                name={`sendTermNoticeVM`}
                                value={values.sendTermNoticeVM}
                                disabled={values.isTermReasonNonPayment}
                            />
                            <GIFormLabel id={`sendTermNoticeVM`} name={`sendTermNoticeVM`}>
                                <FormattedMessage id="member.enrollment_list.premiumhistory.sentTermNotice" />
                            </GIFormLabel>
                        </GIFormControl>
                    </StyledCheckboxField>
                </ConditionalRendering>

                <GIButton
                    isDisabled={!isValid || !isDirty}
                    id="button"
                    onClickHandler={validateAndSaveAddOrChangeTxn}
                    variant="primary"
                    labelId="button.submit"
                    data-testid="submit-button"
                />
                <GIButton
                    id="button"
                    onClickHandler={handleClose}
                    variant="secondary"
                    labelId="button.cancel"
                    data-testid="cancel-button"
                />
            </StyledFooterContainer>
        </>
    );
};
export default AddOeChangeTxnFooter;
